import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Radio, TextField } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { C, FONT, primaryButtonSx, tnum } from './tokens'
import {
  isStepperGroup, itemPrice, modifierIdOf, modifierNameOf, modifierPrice, modifierTotal, variantKeysOf,
} from './menuLine'

const money = n => `$${(Number(n) || 0).toFixed(2)}`
const ghost = { textTransform: 'none', fontWeight: 500, color: C.text, borderRadius: '8px' }
const byOrder = (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)

function GroupHead({ title, required, hint }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
      <Box sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>{title}</Box>
      <Box sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', color: required ? C.danger : C.faint }}>
        {required ? 'REQUIRED' : 'OPTIONAL'}
      </Box>
      {hint && <Box sx={{ fontSize: 12, color: C.muted }}>{hint}</Box>}
    </Box>
  )
}

function Row({ children, onClick, disabled }) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, minHeight: 40, px: 0.5, borderRadius: '8px',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
        '&:hover': disabled ? {} : { bgcolor: C.mutedBg },
      }}
    >
      {children}
    </Box>
  )
}

/**
 * Sizes, modifiers and options for one item, under the same rules as the POS
 * register (ModifierModal.js): radios for single picks, checkboxes for
 * pick-several, steppers where a modifier can be added more than once.
 */
export default function ItemOptionsDialog({ item, menuId, onClose, onAdd }) {
  const sizes = useMemo(() => (item ? variantKeysOf(item) : []), [item])
  const groups = useMemo(() => (item?.modifierGroups || []).slice().sort(byOrder), [item])
  const optionGroups = useMemo(() => (item?.optionGroups || []).slice().sort(byOrder), [item])

  const [size, setSize] = useState(null)
  const [picked, setPicked] = useState({}) // { groupId: { modifierId: count } }
  const [options, setOptions] = useState({}) // { optionGroupId: { choice: true } }
  const [note, setNote] = useState('')
  const [qty, setQty] = useState(1)

  useEffect(() => {
    if (!item) return
    setSize(sizes.length ? sizes[0] : null)
    setPicked({})
    setOptions({})
    setNote('')
    setQty(1)
  }, [item, sizes])

  if (!item) return null

  const countIn = groupId => Object.values(picked[groupId] || {}).reduce((a, b) => a + b, 0)

  const toggle = (group, id, single) => setPicked(prev => {
    const sel = { ...(prev[group._id] || {}) }
    if (single) return { ...prev, [group._id]: { [id]: 1 } }
    if (sel[id]) delete sel[id]
    else {
      if (countIn(group._id) >= (group.maxSelections || 99)) return prev
      sel[id] = 1
    }
    return { ...prev, [group._id]: sel }
  })

  const step = (group, id, delta) => setPicked(prev => {
    const sel = { ...(prev[group._id] || {}) }
    const cur = sel[id] || 0
    if (delta > 0) {
      const max = group.maxSelections || 99
      if (countIn(group._id) >= max || cur >= (group.maxDuplicates || max)) return prev
      sel[id] = cur + 1
    } else if (cur <= 1) delete sel[id]
    else sel[id] = cur - 1
    return { ...prev, [group._id]: sel }
  })

  const pickOption = (og, choice) => setOptions(prev => {
    const single = (og.selectionType || 'SINGLE') !== 'MULTI'
    const cur = { ...(prev[og._id] || {}) }
    if (single) return { ...prev, [og._id]: { [choice]: true } }
    if (cur[choice]) delete cur[choice]
    else cur[choice] = true
    return { ...prev, [og._id]: cur }
  })

  const modifiersTotal = groups.reduce((sum, g) => {
    const sel = picked[g._id] || {}
    const ids = Object.keys(sel).filter(k => sel[k] > 0)
    let t = ids.length && g.additionalCost ? Number(g.additionalCost) : 0
    ids.forEach(id => {
      const m = (g.modifiers || []).find(x => modifierIdOf(x) === id)
      if (m) t += modifierTotal(g, m, sel[id], size)
    })
    return sum + t
  }, 0)
  const unitPrice = itemPrice(item, size) + modifiersTotal

  const missing = groups.find(g => g.isRequired && countIn(g._id) < (g.minSelections || 1))
    || optionGroups.find(og => og.isRequired && !Object.keys(options[og._id] || {}).length)

  const add = () => {
    if (missing) return
    const modifierSelections = groups
      .map(g => {
        const sel = picked[g._id] || {}
        const ids = Object.keys(sel).filter(k => sel[k] > 0)
        if (!ids.length) return null
        return {
          modifierGroupId: g._id,
          modifierGroupName: g.name,
          selectedModifiers: ids.map(id => {
            const m = (g.modifiers || []).find(x => modifierIdOf(x) === id)
            const isItem = m?.memberType === 'ITEM'
            return {
              modifierId: id,
              modifierName: modifierNameOf(m),
              price: modifierPrice(m, size),
              quantity: sel[id],
              ...(isItem ? { memberType: 'ITEM', itemId: id } : {}),
            }
          }),
        }
      })
      .filter(Boolean)
    // Option picks are unpriced; like the POS they ride on the line as text.
    const optionText = optionGroups
      .map(og => {
        const chosen = Object.keys(options[og._id] || {})
        return chosen.length ? `${og.name}: ${chosen.join(', ')}` : null
      })
      .filter(Boolean)
    const instructions = [...optionText, note.trim()].filter(Boolean).join(' · ')
    const sized = size && size !== 'default'
    onAdd({
      itemId: item._id,
      menuId: menuId || item.menuId,
      name: item.name,
      variationKey: sized ? size : null,
      variationTitle: sized ? size : null,
      unitPrice,
      modifierSelections,
      specialInstructions: instructions || null,
      quantity: qty,
    })
  }

  return (
    <Dialog open={!!item} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', fontFamily: FONT, '& button, & input': { fontFamily: FONT } } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 17, color: C.text, pb: 0.5 }}>{item.name}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {sizes.length > 0 && (
          <Box>
            <GroupHead title="Size" required hint="Pick 1" />
            {sizes.map(k => (
              <Row key={k} onClick={() => setSize(k)}>
                <Radio size="small" checked={size === k} sx={{ p: 0.5 }} />
                <Box sx={{ flex: 1, fontSize: 14, color: C.text }}>{k === 'default' ? 'Regular' : k}</Box>
                <Box sx={{ fontSize: 13, color: C.muted, ...tnum }}>{money(itemPrice(item, k))}</Box>
              </Row>
            ))}
          </Box>
        )}

        {groups.map(g => {
          const single = g.selectionType === 'SINGLE' || g.maxSelections === 1
          const stepper = isStepperGroup(g)
          const full = countIn(g._id) >= (g.maxSelections || 99)
          const hint = single ? 'Pick 1' : g.maxSelections ? `Up to ${g.maxSelections}` : null
          return (
            <Box key={g._id}>
              <GroupHead title={g.name} required={g.isRequired} hint={hint} />
              {(g.modifiers || []).filter(m => !m.isHidden).slice().sort(byOrder).map(m => {
                const id = modifierIdOf(m)
                const n = (picked[g._id] || {})[id] || 0
                const price = modifierPrice(m, size)
                if (stepper) {
                  return (
                    <Row key={id}>
                      <Box sx={{ flex: 1, fontSize: 14, color: C.text }}>{modifierNameOf(m)}</Box>
                      {price > 0 && <Box sx={{ fontSize: 13, color: C.muted, ...tnum }}>+{money(price)}</Box>}
                      <IconButton size="small" aria-label={`Remove one ${modifierNameOf(m)}`} disabled={!n} onClick={() => step(g, id, -1)}><RemoveIcon fontSize="small" /></IconButton>
                      <Box sx={{ minWidth: 16, textAlign: 'center', fontSize: 14, ...tnum }}>{n}</Box>
                      <IconButton size="small" aria-label={`Add one ${modifierNameOf(m)}`} disabled={full} onClick={() => step(g, id, 1)}><AddIcon fontSize="small" /></IconButton>
                    </Row>
                  )
                }
                return (
                  <Row key={id} onClick={() => toggle(g, id, single)} disabled={!single && !n && full}>
                    {single ? <Radio size="small" checked={!!n} sx={{ p: 0.5 }} /> : <Checkbox size="small" checked={!!n} sx={{ p: 0.5 }} />}
                    <Box sx={{ flex: 1, fontSize: 14, color: C.text }}>{modifierNameOf(m)}</Box>
                    {price > 0 && <Box sx={{ fontSize: 13, color: C.muted, ...tnum }}>+{money(price)}</Box>}
                  </Row>
                )
              })}
            </Box>
          )
        })}

        {optionGroups.map(og => {
          const single = (og.selectionType || 'SINGLE') !== 'MULTI'
          const choices = Array.isArray(og.choices) ? og.choices : []
          return (
            <Box key={og._id}>
              <GroupHead title={og.name} required={og.isRequired} hint={single ? 'Pick 1' : null} />
              {choices.map(choice => {
                const on = !!(options[og._id] || {})[choice]
                return (
                  <Row key={choice} onClick={() => pickOption(og, choice)}>
                    {single ? <Radio size="small" checked={on} sx={{ p: 0.5 }} /> : <Checkbox size="small" checked={on} sx={{ p: 0.5 }} />}
                    <Box sx={{ fontSize: 14, color: C.text }}>{choice}</Box>
                  </Row>
                )
              })}
            </Box>
          )
        })}

        <TextField
          size="small"
          label="Special instructions"
          value={note}
          onChange={e => setNote(e.target.value)}
          inputProps={{ maxLength: 200 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 'auto' }}>
          <IconButton size="small" aria-label="One fewer" disabled={qty <= 1} onClick={() => setQty(q => Math.max(1, q - 1))}><RemoveIcon fontSize="small" /></IconButton>
          <Box sx={{ minWidth: 20, textAlign: 'center', fontSize: 14, fontWeight: 600, ...tnum }}>{qty}</Box>
          <IconButton size="small" aria-label="One more" onClick={() => setQty(q => Math.min(99, q + 1))}><AddIcon fontSize="small" /></IconButton>
        </Box>
        <Button onClick={onClose} sx={ghost}>Cancel</Button>
        <Button onClick={add} disabled={!!missing} sx={{ ...primaryButtonSx, px: 2 }}>
          {missing ? `Choose ${missing.name}` : `Add · ${money(unitPrice * qty)}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
